import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Save } from 'lucide-react'
import { api, errorMessage } from '@/lib/api'
import type { Order } from '@/types'
import Button from '@/components/ui/Button'
import { Field, Input, Textarea } from '@/components/ui/Field'
import Modal from '@/components/ui/Modal'
import { Select } from '@/components/ui/Select'
import { useToast } from '@/components/ui/toast'
import { invalidateOrderViews } from '@/components/order/lifecycle'

interface TeamMemberRow {
  id: number
  name: string
  role: 'admin' | 'staff' | 'sales'
  status: 'approved' | 'suspended'
}

/**
 * Corrects what the job sheet prints after an order was placed: the job name,
 * the customer's reference and the notes, plus the PIC where the console may
 * change it. Items, prices, dates and payment are deliberately not here -
 * each has its own action or cannot change once placed.
 */
export default function EditOrderDetailsModal({
  order,
  basePath,
  canEditDetails,
  detailsReason,
  canEditPic,
  onClose,
}: {
  order: Order | null
  basePath: '/admin' | '/sales'
  canEditDetails: boolean
  detailsReason?: string | null
  canEditPic: boolean
  onClose: () => void
}) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const [jobName, setJobName] = useState('')
  const [customerRef, setCustomerRef] = useState('')
  const [notes, setNotes] = useState('')
  const [picId, setPicId] = useState('')

  useEffect(() => {
    if (!order) return
    setJobName(order.job_name ?? '')
    setCustomerRef(order.customer_ref ?? '')
    setNotes(order.notes ?? '')
    setPicId(order.sales_user_id ? String(order.sales_user_id) : '')
  }, [order])

  const team = useQuery({
    queryKey: ['admin-users'],
    queryFn: async () => (await api.get<{ data: TeamMemberRow[] }>('/admin/users')).data.data,
    // The response lists every team email, so only a console that may change
    // the PIC asks for it.
    enabled: Boolean(order) && canEditPic,
  })

  const save = useMutation({
    mutationFn: async () => {
      const payload: Record<string, unknown> = {}
      if (canEditDetails) Object.assign(payload, { job_name: jobName, customer_ref: customerRef, notes })
      if (canEditPic && picId && picId !== String(order!.sales_user_id ?? '')) payload.sales_user_id = Number(picId)
      return (await api.patch<{ message?: string }>(`${basePath}/orders/${order!.id}`, payload)).data
    },
    onSuccess: (res) => {
      invalidateOrderViews(queryClient)
      onClose()
      toast.success(res.message ?? 'Saved')
    },
    onError: (err) => toast.error('Could not save the order', errorMessage(err)),
  })

  const picOptions = (team.data ?? [])
    .filter((member) => (member.role === 'sales' || member.role === 'admin') && (member.status === 'approved' || String(member.id) === picId))
    .map((member) => ({ value: String(member.id), label: member.name }))

  return (
    <Modal
      open={Boolean(order)}
      onClose={onClose}
      title={order ? `Edit details - ${order.number}` : ''}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button loading={save.isPending} disabled={!canEditDetails && !canEditPic} onClick={() => save.mutate()}>
            <Save className="size-4" /> Save
          </Button>
        </>
      }
    >
      {order && (
        <div className="space-y-3">
          {canEditDetails ? (
            <>
              <Field label="Job name" htmlFor="edit-job-name">
                <Input
                  id="edit-job-name"
                  value={jobName}
                  onChange={(e) => setJobName(e.target.value)}
                  maxLength={150}
                  placeholder="Blank prints a title made from the items"
                />
              </Field>
              <Field label="Customer order ref" htmlFor="edit-customer-ref">
                <Input id="edit-customer-ref" value={customerRef} onChange={(e) => setCustomerRef(e.target.value)} maxLength={100} />
              </Field>
              <Field label="Notes" htmlFor="edit-notes">
                <Textarea id="edit-notes" value={notes} onChange={(e) => setNotes(e.target.value)} maxLength={2000} />
              </Field>
              <p className="text-xs text-muted">All three print on the job sheet. Reprint it for the floor after saving.</p>
            </>
          ) : (
            <p className="rounded-lg bg-app p-2.5 text-xs text-muted">{detailsReason ?? 'These details can no longer be changed.'}</p>
          )}
          {canEditPic && (
            <Field label="Sales person (PIC)">
              <Select
                value={picId}
                onChange={setPicId}
                placeholder={team.isLoading ? 'Loading team...' : 'Select PIC...'}
                options={picOptions}
              />
            </Field>
          )}
          <p className="text-xs text-muted">
            Items, prices and delivery cannot change after an order is placed. Move the date, cancel or record a
            payment from the order page.
          </p>
        </div>
      )}
    </Modal>
  )
}
